import type { FC, ReactNode } from "react";
import React, { useMemo, useState, useCallback, useContext, useEffect } from "react";
import {
  HiSearch,
  HiBell,
  HiViewGrid,
  HiUserGroup,
  HiDesktopComputer,
  HiUser,
  HiUsers,
  HiPlus,
} from "react-icons/hi";
import { useNavigate } from "react-router-dom";
import Global from "../../globals/globals";
import type { ChatRoom } from "@/types/chat.types";
import { useTranslation } from "react-i18next";

const ROOM_TYPES = {
  all: { label: "All", icon: HiViewGrid, color: "text-purple-600" },
  "agent-to-agent": { label: "Agent", icon: HiDesktopComputer, color: "text-pink-600" },
  "c2a": { label: "User", icon: HiUser, color: "text-green-600" },
  "group-chat": { label: "Group", icon: HiUserGroup, color: "text-yellow-600" },
  conference: { label: "Conference", icon: HiUsers, color: "text-blue-600" },
  unread: { label: "Unread", icon: HiBell, color: "text-red-600" },
} as const;

type RoomTypeKey = keyof typeof ROOM_TYPES;

const formatTimestamp = (unixTsString: any): string => {
  if (!unixTsString) return "";
  const n = Number(unixTsString);
  if (!Number.isFinite(n)) return "";
  const ms = n > 9_999_999_999 ? n : n * 1000;
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

function mapSessionTypeToUiType(session_type_id: number): Exclude<RoomTypeKey, "all" | "unread"> {
  switch (session_type_id) {
    case 0: return "c2a";
    case 1: return "c2a";
    case 2: return "conference";
    case 3: return "group-chat";
    case 4: return "agent-to-agent";
    default: return "c2a";
  }
}

function getDisplayNameFromSessionKey(session_key: string = "", uiType: string, agent?: any, client?: any, participants?: any[]): string {
  if (uiType === "group-chat") {
    if (session_key.startsWith("grpcht:")) {
      const groupName = session_key.substring(7);
      return groupName || session_key;
    }
    const parts = session_key.split("+").map((p) => p.split("@")[0]).filter(Boolean);
    return parts.join(", ") || session_key;
  }

  if (uiType === "conference") {
    if (session_key.startsWith("conf:")) {
      const confName = session_key.substring(5);
      return confName || session_key;
    }
    const parts = session_key.split("+").map((p) => p.split("@")[0]).filter(Boolean);
    return parts.join(", ") || session_key;
  }

  if (uiType === "c2a" && (client?.client_displayname || client?.client_username)) {
    return client.client_displayname || client.client_username;
  }

  if (uiType === "agent-to-agent") {
    // For agent-to-agent, find the receiver from participants where is_current_user is false
    if (participants && participants.length > 0) {
      const receiver = participants.find(p => p.is_current_user === false);
      if (receiver?.details?.agent_displayname || receiver?.details?.agent_username) {
        return receiver.details.agent_displayname || receiver.details.agent_username;
      }
    }
    // Fallback: extract from session_key (e.g., "a2a:user1@domain+user2@domain")
    const parts = session_key.split("+").map((p) => p.split("@")[0]).filter(Boolean);
    return parts[0] || session_key;
  }

  const parts = session_key.split("+").map((p) => p.split("@")[0]).filter(Boolean);
  if (parts.length >= 2) return `${parts[0]} ↔ ${parts[1]}`;
  return parts[0] || session_key;
}

interface RoomRowProps {
  room: ChatRoom;
  isActive: boolean;
  onClick: () => void;
  lastMessage?: string;
}

const RoomRow: FC<RoomRowProps> = ({ room, isActive, onClick, lastMessage = "No messages yet" }) => {
  const sessionKey = room.session_key || "";
  const uiType = mapSessionTypeToUiType(room.session_type_id);
  const displayName = getDisplayNameFromSessionKey(sessionKey, uiType, room?.agent, room?.client, room?.participants);
  const timeTs = room.last_message_at || room.created_at;
  const unreadCount = room.unread_count || 0;
  const isSessionActive = room.session_status === "active";
  const hasUnread = unreadCount > 0;

  return (
    <div
      onClick={(e) => {
        e.stopPropagation(); // Prevent bubbling to parent container
        onClick();
      }}
      className={`
        relative p-2.5 rounded-xl cursor-pointer transition-all duration-200 group
        ${
          isActive
            ? "bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/30 dark:to-purple-900/30 border-2 border-blue-400 dark:border-blue-500 shadow-md"
            : hasUnread
              ? "bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border-2 border-purple-400 dark:border-purple-500 shadow-md hover:shadow-lg"
              : "bg-white/80 dark:bg-gray-700/80 hover:bg-white dark:hover:bg-gray-700 border border-gray-200/50 dark:border-gray-600/50 hover:shadow-md backdrop-blur-sm"
        }
      `}
    >
      <div className="flex items-center gap-2.5">
        <div className="flex-shrink-0 relative">
          <div className="w-8 h-8 bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 rounded-lg flex items-center justify-center shadow-sm">
            <span className="text-white font-medium text-xs">
              {displayName?.charAt(0)?.toUpperCase() || "?"}
            </span>
          </div>
          {isSessionActive && (
            <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-white dark:border-gray-800"></div>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-0.5">
            <h4
              className={`text-sm truncate ${
                unreadCount > 0 ? "font-bold text-gray-900 dark:text-white" : "font-medium text-gray-900 dark:text-white"
              }`}
            >
              {displayName}
            </h4>

            <div className="flex items-center gap-1.5">
              <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">
                {formatTimestamp(timeTs)}
              </span>

              {unreadCount > 0 && !isActive && (
                <span className="px-1.5 py-0.5 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs rounded-full font-medium min-w-[18px] text-center">
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="text-xs truncate flex-1 text-gray-600 dark:text-gray-400">
              <span>{lastMessage}</span>
            </div>
          </div>

          <div className="mt-1">
            <span
              className={`
                inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs font-medium
                ${ROOM_TYPES[uiType]?.color || "text-gray-600"}
              `}
            >
              {React.createElement(ROOM_TYPES[uiType]?.icon || HiUser, {
                className: "w-2.5 h-2.5",
              })}
              {ROOM_TYPES[uiType]?.label || uiType}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * ConversationsSidebarContainer - PARENT COMPONENT
 *
 * This is the main parent component that manages:
 * 1. Room list display (sidebar panel)
 * 2. Selected room state
 * 3. Layout (sidebar + chat area)
 * 4. Renders ChatArea as children when room is selected
 *
 * Architecture:
 * ConversationsSidebarContainer (Parent)
 * ├── Sidebar Panel (room list)
 * └── children (ChatArea or custom content)
 *
 * Usage:
 * <ConversationsSidebarContainer
 *   rooms={rooms}
 *   onRoomSelect={(room) => console.log('Selected:', room)}
 * >
 *   {(selectedRoom) => (
 *     <ChatArea room={selectedRoom} />
 *   )}
 * </ConversationsSidebarContainer>
 */

interface ConversationsSidebarContainerProps {
  rooms: ChatRoom[];
  onRoomSelect?: (room: ChatRoom) => void;
  onClearSelection?: (clearFn: () => void) => void; // Callback to register the clear function
  onRegisterSelectFn?: (selectFn: (room: ChatRoom) => void) => void; // Callback to register the select function
  isLoading?: boolean;
  isLoadingMore?: boolean; // Loading state for pagination
  hasMoreRooms?: boolean; // Whether there are more rooms to load
  onLoadMoreRooms?: () => void; // Callback to load more rooms
  children?: ReactNode | ((selectedRoom: ChatRoom | null) => ReactNode);
  defaultSelectedRoomKey?: string | null;
}

const ConversationsSidebarContainer: FC<ConversationsSidebarContainerProps> = ({
  rooms,
  onRoomSelect,
  onClearSelection,
  onRegisterSelectFn,
  isLoading = false,
  isLoadingMore = false,
  hasMoreRooms = false,
  onLoadMoreRooms,
  children,
  defaultSelectedRoomKey = null,
}) => {
  const navigate = useNavigate();
  const [selectedRoom, setSelectedRoom] = useState<ChatRoom | null>(() => {
    if (defaultSelectedRoomKey) {
      return rooms.find(r => r.session_key === defaultSelectedRoomKey) || null;
    }
    return null;
  });

  useEffect(() => {
    if (!selectedRoom) {
      if (defaultSelectedRoomKey) {
        setSelectedRoom(rooms.find((room) => room.session_key === defaultSelectedRoomKey) || null);
      }
      return;
    }

    const refreshedSelectedRoom = rooms.find((room) => room.session_key === selectedRoom.session_key);
    if (refreshedSelectedRoom && refreshedSelectedRoom !== selectedRoom) {
      setSelectedRoom(refreshedSelectedRoom);
    }
  }, [rooms, selectedRoom, defaultSelectedRoomKey]);

  const [selectedType, setSelectedType] = useState<RoomTypeKey>("all");
  const [searchQuery, setSearchQuery] = useState<string>("");

  const { t: tConversation } = useTranslation("chat");
  const { t: tCommon } = useTranslation("common");

  // Helper function to get last message for a room - directly from API
  const getLastMessageForRoom = useCallback((room: ChatRoom): string => {
    if (room.last_message?.text) {
      const text = room.last_message.text;

      // Check if it's a file/media message (JSON format or specific patterns)
      // File messages often come as JSON objects
       // If control JSON for scheduled call, render friendly preview instead of raw JSON
      if (text.startsWith('{') && text.includes('"job"')) {
        try {
          const parsed = JSON.parse(text);
          const job = parsed.job || (parsed.parameters && parsed.parameters.job) || (parsed.job && parsed.job.str) || null;
          const params = parsed.parameters || parsed.cm || parsed;
          if (String(job).toLowerCase().includes('media-comm-sched') || (params && (params['sched-time'] || params.schedTime))) {
            const schedTime = Number(params['sched-time'] || params.schedTime || 0);
            const start = schedTime ? new Date(schedTime * 1000) : null;
            const commType = params['comm-type'] || params.commType || 'audio';
            const when = start ? start.toLocaleDateString() + ' ' + start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'Soon';
            return `📅 ${commType} scheduled • ${when}`;
          }

          // Check for file transfer job
          if (parsed.job === 'file-transfer' || parsed.job?.includes?.('file')) {
            const fileName = parsed.parameters?.['file-name'] || parsed.parameters?.fileName;
            return fileName ? `📎 ${fileName}` : '📎 File';
          }
        } catch {
          // Not valid JSON, fallthrough
        }
      }

      // Check for common file transfer patterns in text
      if (text.includes('"file-transfer"') || text.includes('"file-name"')) {
        return '📎 File';
      }

      return text;
    }
    return tConversation("conversation.noMessages");
  }, [tConversation]);

  const handleRoomSelect = useCallback((room: ChatRoom) => {
    setSelectedRoom(room);
    if (onRoomSelect) {
      onRoomSelect(room);
    }
  }, [onRoomSelect]);

  // Clear selection when clicking empty space
  const handleClearSelection = useCallback(() => {
    setSelectedRoom(null);
    if (onRoomSelect) {
      onRoomSelect(null as any);
    }
  }, [onRoomSelect]);

  // Register the clear selection function with the parent
  React.useEffect(() => {
    if (onClearSelection) {
      onClearSelection(() => {
        setSelectedRoom(null);
      });
    }
  }, [onClearSelection]);

  // Register the select function with the parent
  React.useEffect(() => {
    if (onRegisterSelectFn) {
      onRegisterSelectFn(handleRoomSelect);
    }
  }, [onRegisterSelectFn, handleRoomSelect]);

  const filteredRooms = useMemo(() => {
    let filtered = rooms;

    if (selectedType !== "all" && selectedType !== "unread") {
      filtered = filtered.filter((r) => mapSessionTypeToUiType(r.session_type_id) === selectedType);
    }

    if (selectedType === "unread") {
      filtered = filtered.filter((r) => (r.unread_count || 0) > 0);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter((r) => {
        const title = getDisplayNameFromSessionKey(
          r.session_key,
          mapSessionTypeToUiType(r.session_type_id),
          r?.agent,
          r?.client,
          r?.participants
        ).toLowerCase();
        return title.includes(query) || String(r.session_key).toLowerCase().includes(query);
      });
    }

    // Sort by last_message_at (if available), fallback to created_at
    const sorted = [...filtered].sort((a, b) => {
      const aTime = a.last_message_at || Number(a.created_at);
      const bTime = b.last_message_at || Number(b.created_at);
      return bTime - aTime;
    });

    return sorted;
  }, [rooms, selectedType, searchQuery]);

  return (
    <div className="flex h-[calc(100vh-4rem)] max-h-[calc(100vh-4rem)] w-full overflow-hidden">
      {/* Sidebar Panel: fixed overlay on mobile, in-flow flex item on desktop */}
      <div className={`
        ${selectedRoom ? 'hidden md:flex' : 'flex'}
        fixed left-0 top-16 bottom-0 w-full z-20
        md:relative md:left-auto md:top-auto md:bottom-auto md:w-80 md:h-full md:z-auto md:shrink-0
        bg-gradient-to-b from-white to-gray-50 dark:from-gray-800 dark:to-gray-900
        shadow-2xl shadow-black/20 dark:shadow-black/40
        border-r border-gray-200 dark:border-gray-700
        flex-col backdrop-blur-sm
      `}>
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500"></div>

        <div className="p-3 border-b border-gray-200 dark:border-gray-700 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-[14px] font-bold text-gray-900 dark:text-white bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent truncate max-w-[200px]" title={tConversation("conversation.title", { customerName: Global.displayname || Global.username })}>
              {tConversation("conversation.title", {
                customerName: Global.displayname || Global.username,
              })}
            </h2>
            <button
              onClick={() => navigate("/agentList")}
              className="p-1.5 text-gray-500 hover:text-white dark:text-gray-400 dark:hover:text-white hover:bg-gradient-to-r hover:from-blue-500 hover:to-purple-500 rounded-lg transition-all duration-200 transform hover:scale-105"
            >
              <HiPlus className="w-4 h-4" />
            </button>
          </div>

          <div className="relative mb-2">
            <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none z-10">
              <HiSearch className="h-4 w-4 text-gray-600 dark:text-gray-300" />
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white/90 dark:bg-gray-700/90 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm hover:shadow-md transition-all duration-200 backdrop-blur-sm"
              placeholder={tConversation("conversation.searchPlaceholder")}
            />
          </div>

          <div className="flex gap-1 justify-between">
            {Object.entries(ROOM_TYPES).map(([type, config]) => {
              const IconComponent = config.icon;
              const isActive = selectedType === (type as RoomTypeKey);

              return (
                <div key={type} className="relative group">
                  <button
                    onClick={() => setSelectedType(type as RoomTypeKey)}
                    className={`
                      relative flex items-center justify-center w-8 h-8 rounded-lg transition-all duration-200 transform hover:scale-110
                      ${
                        isActive
                          ? "bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-md shadow-blue-500/25"
                          : "bg-white/80 dark:bg-gray-700/80 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-600 backdrop-blur-sm"
                      }
                    `}
                    title={config.label}
                  >
                    <IconComponent className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
          </div>

          {isLoading && (
            <div className="mt-2 text-[11px] text-gray-500">{tCommon("loading")}</div>
          )}
        </div>

        <div
          className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent"
          onClick={(e) => {
            // Clear selection only if clicking directly on the container (empty space)
            if (e.target === e.currentTarget) {
              handleClearSelection();
            }
          }}
        >
          {isLoading ? (
            <div className="p-3 text-sm text-gray-500">{tConversation("conversation.loading")}</div>
          ) : filteredRooms.length === 0 ? (
            <div
              className="flex flex-col items-center justify-center h-full p-4 text-center cursor-pointer"
              onClick={handleClearSelection}
            >
              <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-purple-100 dark:from-blue-900/30 dark:to-purple-900/30 rounded-xl flex items-center justify-center mb-3">
                <HiViewGrid className="w-6 h-6 text-gray-400" />
              </div>
              <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                {tConversation("conversation.noConversations")}
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {searchQuery ? tConversation("conversation.noResults") : tConversation("conversation.newConversation")}
              </p>
            </div>
          ) : (
            <div
              className="p-2 space-y-1 min-h-full"
              onClick={(e) => {
                // Clear selection if clicking on padding area (not on a room)
                if (e.target === e.currentTarget) {
                  handleClearSelection();
                }
              }}
            >
              {filteredRooms.map((room) => {
                const isActive = selectedRoom?.session_key === room.session_key;

                return (
                  <RoomRow
                    key={room.session_key}
                    room={room}
                    isActive={isActive}
                    onClick={() => handleRoomSelect(room)}
                    lastMessage={getLastMessageForRoom(room)}
                  />
                );
              })}

              {/* Load More Button */}
              {hasMoreRooms && selectedType === "all" && !searchQuery && (
                <div className="pt-2 pb-4">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onLoadMoreRooms?.();
                    }}
                    disabled={isLoadingMore}
                    className="w-full py-2.5 px-4 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white/80 dark:bg-gray-700/80 hover:bg-gray-100 dark:hover:bg-gray-600 border border-gray-200 dark:border-gray-600 rounded-xl transition-all duration-200 hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isLoadingMore ? (
                      <>
                        <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {tCommon("loading")}
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                        {tConversation("conversation.loadMore") || "Load More"}
                      </>
                    )}
                  </button>
                </div>
              )}

              {/* Loading more indicator at bottom */}
              {isLoadingMore && (
                <div className="py-3 text-center text-sm text-gray-500 dark:text-gray-400">
                  {tCommon("loading")}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Chat Area (children) - Hidden on mobile when no room selected */}
      <div className={`
        ${selectedRoom ? 'flex' : 'hidden md:flex'}
        w-full flex-1 min-w-0 overflow-hidden
      `}>
        <div className="h-full w-full">
          {typeof children === "function" ? children(selectedRoom) : children}
        </div>
      </div>
    </div>
  );
};

export default ConversationsSidebarContainer;
